import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { AlertTriangle, X } from 'lucide-react';
import {
  PaymentPlanDto,
  formatCurrency,
  paymentPlansApi,
  handleApiError
} from '../../services/paymentsService';

interface CancelPaymentPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentPlan: PaymentPlanDto;
  onSuccess: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  padding: 28px;
  max-width: 480px;
  width: 100%;
  box-shadow: var(--shadow-dark);
  direction: rtl;

  @media (max-width: 767px) {
    padding: 20px;
    max-width: 100%;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  border-radius: var(--border-radius-md);
  transition: background 0.15s ease;

  &:hover {
    background: var(--background-secondary);
  }
`;

const WarningBox = styled.div`
  background: rgba(220, 38, 38, 0.06);
  border: 1px solid rgba(220, 38, 38, 0.25);
  border-radius: var(--border-radius-md);
  padding: 14px 16px;
  margin-bottom: 16px;
  color: #991b1b;
  font-size: 14px;
  line-height: 1.6;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const WarningIconWrap = styled.div`
  flex-shrink: 0;
  color: #dc2626;
  display: flex;
  align-items: center;
  padding-top: 2px;
`;

const Summary = styled.div`
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  padding: 14px 16px;
  margin-bottom: 24px;
  font-size: 15px;
  color: var(--text-primary);
  line-height: 1.7;
`;

const SummaryHighlight = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'danger' | 'secondary' }>`
  padding: 10px 22px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  ${props => props.variant === 'danger' ? `
    background: #dc2626;
    color: white;
    border: 1px solid #dc2626;

    &:hover:not(:disabled) {
      background: #b91c1c;
      border-color: #b91c1c;
    }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const CancelPaymentPlanModal: React.FC<CancelPaymentPlanModalProps> = ({
  isOpen,
  onClose,
  paymentPlan,
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const pendingInstallments = paymentPlan.installments?.filter(i => !i.isPaid) || [];
  const pendingCount = pendingInstallments.length;
  const remainingAmount = paymentPlan.remainingAmount;

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      await paymentPlansApi.cancel(paymentPlan.id);
      toast.success('תוכנית התשלומים בוטלה בהצלחה');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={() => !isSubmitting && onClose()}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <HeaderRow>
          <Title>סגירת תוכנית תשלומים</Title>
          <CloseButton onClick={onClose} disabled={isSubmitting} aria-label="סגור">
            <X size={20} />
          </CloseButton>
        </HeaderRow>

        <WarningBox>
          <WarningIconWrap>
            <AlertTriangle size={18} />
          </WarningIconWrap>
          <div>
            פעולה זו תבטל את הוראת הקבע הפעילה ב-Cardcom וסטטוס התוכנית ישתנה ל"בוטל".
            פעולה זו אינה הפיכה.
          </div>
        </WarningBox>

        <Summary>
          קיימים <SummaryHighlight>{pendingCount}</SummaryHighlight> תשלומים שטרם בוצעו
          בסך <SummaryHighlight>{formatCurrency(remainingAmount)}</SummaryHighlight>.<br />
          האם לסגור את התוכנית?
        </Summary>

        <ButtonGroup>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            ביטול
          </Button>
          <Button variant="danger" onClick={handleCancel} disabled={isSubmitting}>
            {isSubmitting ? 'סוגר...' : 'סגור תוכנית'}
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default CancelPaymentPlanModal;
